import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getVisits, exportVisits, getStores, getMercaderistas } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Download, Eye, Search, Clock, MapPin, Package, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';

const STATUS_OPTIONS = [
  { value: 'CREADA', label: 'Creada', color: 'bg-slate-100 text-slate-700' },
  { value: 'EN_PROGRESO', label: 'En Progreso', color: 'bg-blue-100 text-blue-700' },
  { value: 'PENDIENTE', label: 'Pendiente', color: 'bg-amber-100 text-amber-700' },
  { value: 'COMPLETA', label: 'Completa', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'CERRADA', label: 'Cerrada', color: 'bg-slate-100 text-slate-700' },
  { value: 'INCUMPLIDA', label: 'Incumplida', color: 'bg-red-100 text-red-700' }
];

export default function VisitsPage() {
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [stores, setStores] = useState([]);
  const [mercaderistas, setMercaderistas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  const [filters, setFilters] = useState({
    status: '',
    store_id: '',
    user_id: '',
    date_from: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    date_to: format(new Date(), 'yyyy-MM-dd')
  });

  useEffect(() => {
    loadVisits();
    loadDropdownData();
  }, [filters]);

  const loadVisits = async () => {
    try {
      const params = {};
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.store_id && filters.store_id !== 'all') params.store_id = filters.store_id;
      if (filters.user_id && filters.user_id !== 'all') params.user_id = filters.user_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      
      const response = await getVisits(params);
      setVisits(response.data);
    } catch (error) {
      toast.error('Error cargando visitas');
    } finally {
      setLoading(false);
    }
  };

  const loadDropdownData = async () => {
    try {
      const [storesRes, mercaRes] = await Promise.all([
        getStores({ active: true }),
        getMercaderistas()
      ]);
      setStores(storesRes.data);
      setMercaderistas(mercaRes.data);
    } catch (error) {
      toast.error('Error cargando filtros');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportVisits({
        date_from: filters.date_from,
        date_to: filters.date_to,
        store_id: filters.store_id || undefined,
        user_id: filters.user_id || undefined
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `visitas_${filters.date_from}_${filters.date_to}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error exportando datos');
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status);
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.color || 'bg-slate-100 text-slate-700'}`}>
        {statusConfig?.label || status}
      </span>
    );
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="space-y-6" data-testid="visits-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Visitas</h1>
          <p className="text-slate-500">Monitorea todas las visitas de mercaderistas</p>
        </div>
        <Button 
          onClick={handleExport} 
          variant="outline" 
          disabled={exporting}
          data-testid="export-visits-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Desde</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                data-testid="filter-date-from"
              />
            </div>
            <div className="space-y-2">
              <Label>Hasta</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                data-testid="filter-date-to"
              />
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tienda</Label>
              <Select value={filters.store_id} onValueChange={(value) => setFilters({...filters, store_id: value})}>
                <SelectTrigger data-testid="filter-store">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {stores.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Mercaderista</Label>
              <Select value={filters.user_id} onValueChange={(value) => setFilters({...filters, user_id: value})}>
                <SelectTrigger data-testid="filter-mercaderista">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {mercaderistas.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {[...Array(5)].map((_, i) => <Skeleton key={`visits-skeleton-${i}`} className="h-12" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Tienda</TableHead>
                  <TableHead>Mercaderista</TableHead>
                  <TableHead>Check-in</TableHead>
                  <TableHead>Duración</TableHead>
                  <TableHead>SKUs</TableHead>
                  <TableHead>Fotos</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visits.map((visit) => (
                  <TableRow 
                    key={visit.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    data-testid={`visit-row-${visit.id}`}
                  >
                    <TableCell className="font-medium">{visit.store_name}</TableCell>
                    <TableCell>{visit.user_name}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {visit.checkin_at ? format(new Date(visit.checkin_at), 'dd/MM HH:mm') : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span className="font-mono text-sm">{formatDuration(visit.duration_minutes)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Package className="w-3 h-3 text-slate-400" />
                        <span className="font-mono text-sm">{visit.sku_reports_count || 0}</span>
                        {visit.low_stock_count > 0 && (
                          <Badge variant="destructive" className="ml-1 text-xs">
                            {visit.low_stock_count} bajo
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Camera className="w-3 h-3 text-slate-400" />
                        <span className="font-mono text-sm">{visit.evidence_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(visit.status)}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => navigate(`/visits/${visit.id}`)}
                        data-testid={`view-visit-${visit.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {visits.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      No hay visitas para los filtros seleccionados
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
